import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaHome,
  FaUser,
  FaBook,
  FaClipboardCheck,
  FaFileInvoice,
  FaCalendarCheck,
  FaAward,
  FaPhone,
  FaSchool,
  FaMapMarkerAlt,
  FaShoppingBag,
  FaMoneyBillWave
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { authAPI, enrollmentAPI, attendanceAPI, invoiceAPI, paymentAPI, productAPI, getImageUrl } from '../services/api';
import { formatApiError } from '../utils/errorUtils';
import DashboardLayout from '../components/DashboardLayout';
import StatCardGrid from '../components/StatCardGrid';
import BatchesPage from './BatchesPage';
import EnrollmentsPage from './EnrollmentsPage';
import AttendancePage from './AttendancePage';
import InvoicesPage from './InvoicesPage';
import PaymentsPage from './PaymentsPage';
import ProductsPage from './ProductsPage';
import './StudentDashboard.css';

function StudentDashboard() {
  const { user, logout, updateStoredUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.focusTab || 'dashboard');
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [studentMetrics, setStudentMetrics] = useState({
    enrollments: 0,
    attendance: 0,
    purchases: 0,
    payments: 0,
    pendingInvoices: 0,
    paidInvoices: 0,
    totalOutstanding: 0
  });
  const [profileForm, setProfileForm] = useState({
    Email: '',
    FirstName: '',
    LastName: '',
    DOB: '',
    Gender: 'M',
    FatherName: '',
    MotherName: '',
    SchoolName: '',
    SchoolGrade: '',
    Address: '',
    ContactNumber: '',
    EmergencyContact: '',
    RegistrationDate: '',
    SkillLevel: 'Beginner',
    TermsAccepted: false,
    PhotoPath: ''
  });
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState(
    location.state?.showProfileNotice ? 'Registration completed. Please update your profile now.' : ''
  );
  const [savingProfile, setSavingProfile] = useState(false);
  const [enrolledBatchIds, setEnrolledBatchIds] = useState([]);
  const [enrollingBatchId, setEnrollingBatchId] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const loadProfile = async () => {
      setDashboardLoading(true);
      try {
        const studentId = user?.StudentID;
        const [profileResponse, enrollmentsResponse, attendanceResponse, invoicesResponse, paymentsResponse, purchasesResponse] = await Promise.all([
          authAPI.getStudentProfile(),
          enrollmentAPI.getByStudent(studentId),
          attendanceAPI.getByStudent(studentId),
          invoiceAPI.getByStudent(studentId),
          paymentAPI.getAll(),
          productAPI.getMyPurchases()
        ]);

        const nextProfile = profileResponse.data.user || {};
        const enrollments = enrollmentsResponse.data || [];
        const attendance = attendanceResponse.data || [];
        const invoices = invoicesResponse.data || [];
        const payments = paymentsResponse.data || [];
        const purchases = purchasesResponse.data || [];

        setProfile(nextProfile);
        setProfileForm({
          Email: nextProfile.Email || '',
          FirstName: nextProfile.FirstName || '',
          LastName: nextProfile.LastName || '',
          DOB: nextProfile.DOB ? String(nextProfile.DOB).split('T')[0] : '',
          Gender: nextProfile.Gender || 'M',
          FatherName: nextProfile.FatherName || '',
          MotherName: nextProfile.MotherName || '',
          SchoolName: nextProfile.SchoolName || '',
          SchoolGrade: nextProfile.SchoolGrade || '',
          Address: nextProfile.Address || '',
          ContactNumber: nextProfile.ContactNumber || '',
          EmergencyContact: nextProfile.EmergencyContact || '',
          RegistrationDate: nextProfile.RegistrationDate ? String(nextProfile.RegistrationDate).split('T')[0] : '',
          SkillLevel: nextProfile.SkillLevel || 'Beginner',
          TermsAccepted: Boolean(nextProfile.TermsAccepted),
          PhotoPath: nextProfile.PhotoPath || ''
        });
        setEnrolledBatchIds(enrollments.map((item) => item.BatchID));
        setStudentMetrics({
          enrollments: enrollments.length,
          attendance: attendance.length,
          purchases: purchases.length,
          payments: payments.length,
          pendingInvoices: invoices.filter((item) => ['Pending', 'Partial'].includes(item.ComputedStatus || item.Status)).length,
          paidInvoices: invoices.filter((item) => (item.ComputedStatus || item.Status) === 'Paid').length,
          totalOutstanding: invoices.reduce((sum, item) => sum + Number(item.BalanceDue || 0), 0)
        });
        setProfileError('');
      } catch (err) {
        setProfileError(formatApiError(err, 'Failed to load student details'));
      } finally {
        setDashboardLoading(false);
      }
    };

    if (user?.StudentID) {
      loadProfile();
    } else {
      setDashboardLoading(false);
    }
  }, [user?.StudentID]);

  const profileData = useMemo(() => profile || user || {}, [profile, user]);
  const studentName = profileData?.FirstName ? `${profileData.FirstName} ${profileData.LastName || ''}`.trim() : 'User';

  const navLinks = useMemo(
    () => [
      { id: 'dashboard', label: 'Dashboard', icon: FaHome },
      { id: 'profile', label: 'My Profile', icon: FaUser },
      { id: 'batches', label: 'Batches', icon: FaBook },
      { id: 'enrollments', label: 'Enrollments', icon: FaClipboardCheck, badge: studentMetrics.enrollments },
      { id: 'products', label: 'Club Store', icon: FaShoppingBag, badge: studentMetrics.purchases },
      { id: 'attendance', label: 'Attendance', icon: FaCalendarCheck, badge: studentMetrics.attendance },
      { id: 'invoices', label: 'Invoices', icon: FaFileInvoice, badge: studentMetrics.pendingInvoices > 0 ? `${studentMetrics.pendingInvoices} Due` : null },
      { id: 'payments', label: 'Payments', icon: FaMoneyBillWave }
    ],
    [studentMetrics]
  );

  const profileStats = useMemo(
    () => [
      { id: 'level', label: 'Skill Level', value: profileData?.SkillLevel || 'Beginner', icon: FaAward, color: 'indigo' },
      { id: 'enrollments', label: 'Active Batches', value: studentMetrics.enrollments, icon: FaBook, color: 'blue', onClick: () => setActiveTab('enrollments') },
      { id: 'attendance', label: 'Classes Attended', value: studentMetrics.attendance, icon: FaCalendarCheck, color: 'emerald', onClick: () => setActiveTab('attendance') },
      { id: 'payments', label: 'Payments Made', value: studentMetrics.payments, icon: FaMoneyBillWave, color: 'purple', onClick: () => setActiveTab('payments') },
      { id: 'store', label: 'Items Bought', value: studentMetrics.purchases, icon: FaShoppingBag, color: 'amber', onClick: () => setActiveTab('products') },
      { id: 'due', label: 'Balance Due', value: `Rs ${Number(studentMetrics.totalOutstanding || 0).toFixed(2)}`, icon: FaMoneyBillWave, color: studentMetrics.totalOutstanding > 0 ? 'rose' : 'emerald', onClick: () => setActiveTab('invoices') }
    ],
    [profileData, studentMetrics]
  );

  const extraDetails = [
    { label: 'Phone Number', value: profileData?.ContactNumber || 'N/A', icon: <FaPhone /> },
    { label: 'Emergency Contact', value: profileData?.EmergencyContact || 'N/A', icon: <FaPhone /> },
    { label: 'School', value: profileData?.SchoolName || 'N/A', icon: <FaSchool /> },
    { label: 'School Grade', value: profileData?.SchoolGrade || 'N/A', icon: <FaSchool /> },
    { label: 'Address', value: profileData?.Address || 'N/A', icon: <FaMapMarkerAlt /> },
    { label: 'Paid Invoices', value: studentMetrics.paidInvoices, icon: <FaFileInvoice /> },
    { label: 'Pending Invoices', value: studentMetrics.pendingInvoices, icon: <FaClipboardCheck /> },
    { label: 'Registration Date', value: profileData?.RegistrationDate ? String(profileData.RegistrationDate).split('T')[0] : 'N/A', icon: <FaCalendarCheck /> }
  ];

  const handleProfileInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: type === 'file' && files?.length ? files[0] : type === 'checkbox' ? checked : value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileError('');
    setProfileMessage('');

    try {
      const data = new FormData();
      Object.entries(profileForm).forEach(([key, value]) => {
        if (key === 'PhotoPath') {
          if (value instanceof File) {
            data.append(key, value);
          }
          return;
        }
        data.append(key, value);
      });

      const response = await authAPI.updateStudentProfile(data);
      const nextProfile = response.data.user;
      setProfile(nextProfile);
      updateStoredUser({
        ...user,
        Email: nextProfile.Email,
        FirstName: nextProfile.FirstName,
        LastName: nextProfile.LastName,
        PhotoPath: nextProfile.PhotoPath,
        SkillLevel: nextProfile.SkillLevel
      });
      setProfileForm((prev) => ({
        ...prev,
        PhotoPath: nextProfile.PhotoPath || prev.PhotoPath
      }));
      setProfileMessage('User profile updated successfully.');
    } catch (err) {
      setProfileError(formatApiError(err, 'Failed to update profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const openPaymentsTab = () => {
    setActiveTab('payments');
    setProfileMessage('Enrollment created. Complete the fee payment from the Payments tab.');
  };

  const handleEnroll = async (batch, options = {}) => {
    try {
      setEnrollingBatchId(batch.BatchID);
      setProfileError('');
      const createResponse = await enrollmentAPI.create({
        BatchID: batch.BatchID,
        Status: 'Active',
        PaymentTiming: options.paymentTiming || 'Later'
      });
      const enrollmentListResponse = await enrollmentAPI.getByStudent(profileData.StudentID || user?.StudentID);
      const enrollments = enrollmentListResponse.data || [];
      setEnrolledBatchIds(enrollments.map((item) => item.BatchID));
      setStudentMetrics((prev) => ({ ...prev, enrollments: enrollments.length }));
      if ((options.paymentTiming || 'Later') === 'Now') {
        openPaymentsTab();
      } else {
        setActiveTab('enrollments');
        setProfileMessage('Enrollment created and invoice added to your account.');
      }
      return createResponse.data;
    } catch (err) {
      setProfileError(formatApiError(err, 'Failed to enroll in batch'));
      return null;
    } finally {
      setEnrollingBatchId(null);
    }
  };

  return (
    <DashboardLayout
      brandName="Epsilon Skating Club"
      userProfile={{
        name: studentName,
        email: profileData?.Email || user?.Email || '',
        avatar: profileData?.PhotoPath ? getImageUrl(profileData.PhotoPath) : null
      }}
      roleBadge="Student"
      navLinks={navLinks}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onLogout={handleLogout}
      headerTitle={activeTab === 'dashboard' ? 'Student Dashboard' : navLinks.find(l => l.id === activeTab)?.label || 'Student Portal'}
      headerSubtitle={activeTab === 'dashboard' ? 'Track your profile, class schedules, attendance, store purchases, and fee payments.' : ''}
    >
      {profileError && <div className="alert alert-error">{profileError}</div>}
      {profileMessage && <div className="alert alert-success">{profileMessage}</div>}

      {dashboardLoading ? (
        <div className="loading loading-panel">Loading student portal...</div>
      ) : (
        <>
          {/* MAIN DASHBOARD OVERVIEW TAB (Stat cards ONLY shown here!) */}
          {activeTab === 'dashboard' && (
            <div className="sdb-dashboard-tab">
              <div className="mb-6">
                <StatCardGrid stats={profileStats} loading={dashboardLoading} layoutMode="grid" />
              </div>

              <div className="quick-start-section container mt-4">
                <h3 className="page-title text-xl mb-3">Quick Navigation</h3>
                <div className="quick-links">
                  <button type="button" className="quick-link" onClick={() => setActiveTab('profile')}>
                    <FaUser /> My Profile
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('batches')}>
                    <FaBook /> Available Batches
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('enrollments')}>
                    <FaClipboardCheck /> My Enrollments ({studentMetrics.enrollments})
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('products')}>
                    <FaShoppingBag /> Club Store ({studentMetrics.purchases})
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('attendance')}>
                    <FaCalendarCheck /> Attendance Records ({studentMetrics.attendance})
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('invoices')}>
                    <FaFileInvoice /> Fee Invoices ({studentMetrics.pendingInvoices > 0 ? `${studentMetrics.pendingInvoices} Pending` : 'All Paid'})
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <section className="sdb-profile-card">
              <div className="sdb-section-header">
                <h2>My Profile</h2>
                <p>Keep your personal details, emergency contacts, and profile image up to date.</p>
              </div>

              <div className="sdb-profile-layout">
                <div className="sdb-profile-column">
                  <section className="sdb-section-card">
                    <div className="sdb-profile-grid">
                      <div><label>First Name</label><p>{profileData?.FirstName || 'N/A'}</p></div>
                      <div><label>Last Name</label><p>{profileData?.LastName || 'N/A'}</p></div>
                      <div><label>Email</label><p>{profileData?.Email || 'N/A'}</p></div>
                      <div><label>Student ID</label><p>{profileData?.StudentID || 'N/A'}</p></div>
                      <div><label>Skill Level</label><p>{profileData?.SkillLevel || 'N/A'}</p></div>
                      <div><label>Status</label><p className="sdb-status-pill">{profileData?.IsActive === false ? 'Inactive' : 'Active'}</p></div>
                    </div>
                  </section>

                  <section className="sdb-section-card">
                    <div className="sdb-detail-grid">
                      {extraDetails.map((item) => (
                        <article key={item.label} className="sdb-detail-card">
                          <span className="sdb-detail-icon">{item.icon}</span>
                          <div>
                            <label>{item.label}</label>
                            <p>{item.value}</p>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                </div>

                <div className="sdb-profile-column sdb-profile-editor">
                  <section className="sdb-section-card">
                    <div className="sdb-photo-block sdb-photo-card">
                      <p>Profile Photo</p>
                      {profileData?.PhotoPath ? (
                        <img src={getImageUrl(profileData.PhotoPath)} alt={studentName} />
                      ) : (
                        <div className="sdb-photo-placeholder">
                          <FaUser />
                          <span>No profile image yet</span>
                        </div>
                      )}
                    </div>

                    <form onSubmit={handleProfileSubmit} className="sdb-profile-form">
                      <h3>Update Profile</h3>
                      <div className="form-row">
                        <div className="form-group"><label>Email</label><input type="email" name="Email" value={profileForm.Email} onChange={handleProfileInputChange} required /></div>
                        <div className="form-group"><label>Skill Level</label><select name="SkillLevel" value={profileForm.SkillLevel} onChange={handleProfileInputChange}><option value="Beginner">Beginner</option><option value="Intermediate">Intermediate</option><option value="Advanced">Advanced</option></select></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>First Name</label><input type="text" name="FirstName" value={profileForm.FirstName} onChange={handleProfileInputChange} /></div>
                        <div className="form-group"><label>Last Name</label><input type="text" name="LastName" value={profileForm.LastName} onChange={handleProfileInputChange} /></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>Date of Birth</label><input type="date" name="DOB" value={profileForm.DOB} onChange={handleProfileInputChange} /></div>
                        <div className="form-group"><label>Gender</label><select name="Gender" value={profileForm.Gender} onChange={handleProfileInputChange}><option value="M">Male</option><option value="F">Female</option><option value="Other">Other</option></select></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>Phone Number</label><input type="text" name="ContactNumber" value={profileForm.ContactNumber} onChange={handleProfileInputChange} /></div>
                        <div className="form-group"><label>Emergency Contact</label><input type="text" name="EmergencyContact" value={profileForm.EmergencyContact} onChange={handleProfileInputChange} /></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>Father Name</label><input type="text" name="FatherName" value={profileForm.FatherName} onChange={handleProfileInputChange} /></div>
                        <div className="form-group"><label>Mother Name</label><input type="text" name="MotherName" value={profileForm.MotherName} onChange={handleProfileInputChange} /></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>School Name</label><input type="text" name="SchoolName" value={profileForm.SchoolName} onChange={handleProfileInputChange} /></div>
                        <div className="form-group"><label>School Grade</label><input type="text" name="SchoolGrade" value={profileForm.SchoolGrade} onChange={handleProfileInputChange} /></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>Registration Date</label><input type="date" name="RegistrationDate" value={profileForm.RegistrationDate} onChange={handleProfileInputChange} /></div>
                        <div className="form-group">
                          <label>Profile Image</label>
                          <input type="file" name="PhotoPath" onChange={handleProfileInputChange} accept="image/*" />
                          <p className="form-help">Use a square JPG or PNG image for the cleanest display.</p>
                          {profileForm.PhotoPath && (
                            <div className="upload-preview">
                              <img
                                src={profileForm.PhotoPath instanceof File ? URL.createObjectURL(profileForm.PhotoPath) : getImageUrl(profileForm.PhotoPath)}
                                alt="Profile preview"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="form-group"><label>Address</label><textarea name="Address" value={profileForm.Address} onChange={handleProfileInputChange} rows="3" /></div>
                      <label className="sdb-checkbox"><input type="checkbox" name="TermsAccepted" checked={profileForm.TermsAccepted} onChange={handleProfileInputChange} /> Terms and conditions accepted</label>
                      <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={savingProfile}>{savingProfile ? 'Saving...' : 'Save Profile'}</button></div>
                    </form>
                  </section>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'batches' && <BatchesPage readOnly title="Available Batches" subtitle="Students can view class details, choose payment now or later, and enroll without editing academy records." onEnroll={handleEnroll} enrolledBatchIds={enrolledBatchIds} enrollingBatchId={enrollingBatchId} onOpenPayments={openPaymentsTab} />}
          {activeTab === 'enrollments' && <EnrollmentsPage readOnly studentId={profileData?.StudentID || user?.StudentID} title="My Enrollments" subtitle="Track your current batch enrollments and status updates." />}
          {activeTab === 'products' && <ProductsPage readOnly title="Club Store" subtitle="Browse available products, purchase items, and review your purchase history." />}
          {activeTab === 'attendance' && <AttendancePage readOnly studentId={profileData?.StudentID || user?.StudentID} title="My Attendance" subtitle="Review your attendance records by batch and class date." />}
          {activeTab === 'invoices' && <InvoicesPage readOnly studentId={profileData?.StudentID || user?.StudentID} title="My Invoices" subtitle="See paid, pending, and partial billing records with balance details." />}
          {activeTab === 'payments' && <PaymentsPage readOnly studentId={profileData?.StudentID || user?.StudentID} title="My Payments" subtitle="Review payments already recorded against your invoices and track remaining balances." />}
        </>
      )}
    </DashboardLayout>
  );
}

export default StudentDashboard;
